import { ref } from 'vue';

import { accountService } from '@exchange/libs/account/service/src';
import { authService, capAuth2NoToastAuthenticateErrors, AuthError } from '@exchange/libs/auth/service/src';
import { isLocalStorageQuoteExceeded } from '@exchange/libs/auth/service/src/lib/auth-errors';
import { toastManagerInstance, SimpleToast } from '@exchange/libs/toasts/src';
import { capBrowser, capIsNativePlatform } from '@exchange/libs/utils/capacitor/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import { serverClientTimeService } from '@exchange/libs/utils/server-client-time/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';
import router from '@exchange/routing/router';

const useLogInOut = () => {
  const refElem = ref<HTMLElement>();
  const processingLogOut = ref(false);
  const isNative = capIsNativePlatform();

  /**
   * PROFE-635 temporary make signup behave as login to avoid cookie banner being shows on broker

  const temporarySolutionSignup = () => {
    if (isNative) {
      return login({ noToastPlease: true });
    }

    return signup();
  };
   */

  const waitForAccountId = () => accountService.getAccountId({ useSubaccount: false });

  const login = async ({ noToastPlease = false } = {}) => {
    try {
      serverClientTimeService.resetClientToServerTime();
      const next = await authService.signIn();

      await waitForAccountId();

      if (next) {
        router.replace(next);
      }
    } catch (e) {
      logger.error(e);

      if (noToastPlease) {
        return;
      }

      if (isNative) {
        const eMessage = (e as AuthError)?.message;

        if (eMessage && capAuth2NoToastAuthenticateErrors.includes(eMessage)) {
          return;
        }
      }

      const localStorageQuotaExceeded = isLocalStorageQuoteExceeded(e);
      const message = !isNative && localStorageQuotaExceeded ? 'modules.login.toasts.failed.localStorageMessage' : 'modules.login.toasts.failed.message';

      toastManagerInstance.addToast({
        content: SimpleToast,
        props: {
          variant: 'failed',
          title: 'modules.login.toasts.failed.title',
          message,
        },
      });
    }
  };

  const signup = () => {
    const isUniversalLogin = launchdarkly.flags['universal-login'].value;

    if (!isUniversalLogin) {
      login();
      return;
    }

    const signupUrl = `${process.env.VUE_APP_ACCOUNTS_URL}/register`;

    if (isNative) {
      capBrowser.open({ url: signupUrl });
    } else {
      window.location.href = signupUrl;
    }
  };

  const reportLogoutOnXSDoing = (value: boolean, isXs: boolean) => {
    if (!isXs) {
      return;
    }

    processingLogOut.value = value;

    const event = new CustomEvent('logout-on-xs', {
      detail: {
        doing: value,
      },
    });

    refElem.value?.dispatchEvent(event);
  };

  const logout = async (isXs: boolean) => {
    try {
      reportLogoutOnXSDoing(true, isXs);
      await authService.signOut();
    } catch (e) {
      logger.error(e);
      toastManagerInstance.addToast({
        content: SimpleToast,
        props: {
          variant: 'failed',
          title: 'fundamentals.error.title',
          message: 'fundamentals.error.text',
        },
      });
    } finally {
      reportLogoutOnXSDoing(false, isXs);
    }
  };

  return {
    refElem,
    processingLogOut,
    login,
    signup,
    waitForAccountId,
    logout,
  };
};

export default useLogInOut;
